import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../contexts/LanguageContext';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { CheckCircle2, XCircle, Wallet, FileText, RefreshCw, Search } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_STYLES = {
  pending: 'bg-amber-100 text-amber-800',
  confirmed: 'bg-green-100 text-green-800',
  rejected: 'bg-red-100 text-red-800',
  expired: 'bg-gray-100 text-gray-700',
};

const formatDate = (iso) => {
  if (!iso) return '';
  try { return new Date(iso).toLocaleString(); } catch (_) { return iso; }
};

export const AdminPaymentsView = () => {
  const { t } = useLanguage();
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState('pending');
  const [providerFilter, setProviderFilter] = useState('all');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [actioning, setActioning] = useState(null);
  const [noteById, setNoteById] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter !== 'all') params.status = filter;
      if (providerFilter !== 'all') params.provider = providerFilter;
      const r = await axios.get(`${API}/admin/payments`, { params, withCredentials: true });
      setItems(r.data || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load payments');
    } finally {
      setLoading(false);
    }
  }, [filter, providerFilter]);

  useEffect(() => { load(); }, [load]);

  const filteredItems = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((p) => {
      const bag = [
        p.reference, p.user_name, p.user_email, p.user_phone,
        p.omt_receipt, p.external_id, p.package_id, p.package_name_en, p.package_name_ar,
        p.admin_note,
      ].filter(Boolean).join(' ').toLowerCase();
      return bag.includes(q);
    });
  }, [items, query]);

  const act = async (payment, action) => {
    setActioning(payment.id);
    try {
      const admin_note = noteById[payment.id] || '';
      await axios.post(
        `${API}/admin/payments/${payment.id}/verify`,
        { action, admin_note },
        { withCredentials: true }
      );
      toast.success(action === 'confirm' ? t('تم التأكيد', 'Confirmed') : t('تم الرفض', 'Rejected'));
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Action failed');
    } finally {
      setActioning(null);
    }
  };

  const openReceipt = (payment) => {
    // The receipt route returns the file inline; open in new tab.
    if (!payment.receipt_url) return;
    window.open(`${process.env.REACT_APP_BACKEND_URL}${payment.receipt_url}`, '_blank');
  };

  return (
    <div className="space-y-4" data-testid="admin-payments-view">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-2xl font-bold text-[#2c3e50] flex items-center gap-2">
          <Wallet size={22} /> {t('المدفوعات', 'Payments')}
        </h2>
        <div className="flex items-center gap-2 flex-wrap">
          {['pending', 'confirmed', 'rejected', 'expired', 'all'].map((s) => (
            <button
              key={s}
              onClick={() => setFilter(s)}
              data-testid={`filter-${s}`}
              className={`px-3 py-1 rounded-full text-xs font-bold border transition
                ${filter === s ? 'bg-[#1e88e5] text-white border-[#1e88e5]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
            >
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
          <button onClick={load} className="p-1 rounded-full hover:bg-gray-100" data-testid="reload-payments"><RefreshCw size={16} /></button>
        </div>
      </div>

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <Input
            placeholder="Search reference / OMT receipt / user…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="pl-9"
            data-testid="search-payments"
          />
        </div>
        {['all', 'omt', 'whish'].map((p) => (
          <button
            key={p}
            onClick={() => setProviderFilter(p)}
            data-testid={`filter-provider-${p}`}
            className={`px-3 py-1 rounded-full text-xs font-bold border transition uppercase
              ${providerFilter === p ? 'bg-[#0d2c54] text-white border-[#0d2c54]' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}
          >
            {p}
          </button>
        ))}
      </div>

      {loading && <div className="text-center text-gray-500 py-8">Loading…</div>}
      {!loading && filteredItems.length === 0 && (
        <Card className="p-8 text-center text-gray-500" data-testid="empty-payments">
          {t('لا توجد مدفوعات', 'No payments found')}
        </Card>
      )}

      {!loading && filteredItems.map((p) => (
        <Card key={p.id} className="p-4" data-testid={`payment-row-${p.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-2 mb-1 flex-wrap">
                <span className="font-mono font-bold text-[#e53935]" data-testid={`payment-ref-${p.id}`}>{p.reference}</span>
                <span className={`px-2 py-0.5 rounded-full text-xs font-bold ${STATUS_STYLES[p.status] || 'bg-gray-100'}`}>
                  {p.status}
                </span>
                <span className="text-xs uppercase text-gray-500">{p.provider}</span>
                {p.omt_receipt && (
                  <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-blue-50 text-blue-700 border border-blue-200" data-testid={`omt-receipt-${p.id}`}>
                    OMT #{p.omt_receipt}
                  </span>
                )}
              </div>
              <div className="text-sm text-gray-700">
                <b>${p.amount}</b> {p.currency} · {p.package_name_en || p.package_id}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                {p.user_name || '—'} · {p.user_phone || p.user_email || '—'} · {formatDate(p.created_at)}
              </div>
              {(p.external_id || p.omt_session_id) && (
                <div className="text-xs text-gray-400 mt-1 font-mono">
                  txn: {p.external_id || p.omt_session_id}
                </div>
              )}
              {p.admin_note && (
                <div className="mt-1 text-xs italic text-gray-500">Note: {p.admin_note}</div>
              )}
            </div>

            <div className="flex flex-col gap-2 min-w-[240px]">
              {p.receipt_url && (
                <Button size="sm" variant="outline" onClick={() => openReceipt(p)} data-testid={`view-receipt-${p.id}`}>
                  <FileText size={14} className="mr-1" /> {t('عرض الإيصال', 'View receipt')}
                </Button>
              )}
              {p.status === 'pending' && (
                <>
                  <Input
                    placeholder={t('ملاحظة (اختياري)', 'Note (optional)')}
                    value={noteById[p.id] || ''}
                    onChange={(e) => setNoteById({ ...noteById, [p.id]: e.target.value })}
                    className="text-xs h-8"
                    data-testid={`note-input-${p.id}`}
                  />
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      className="flex-1 bg-green-600 hover:bg-green-700"
                      onClick={() => act(p, 'confirm')}
                      disabled={actioning === p.id}
                      data-testid={`confirm-payment-${p.id}`}
                    >
                      <CheckCircle2 size={14} className="mr-1" /> {t('تأكيد', 'Confirm')}
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      className="flex-1"
                      onClick={() => act(p, 'reject')}
                      disabled={actioning === p.id}
                      data-testid={`reject-payment-${p.id}`}
                    >
                      <XCircle size={14} className="mr-1" /> {t('رفض', 'Reject')}
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
};

export default AdminPaymentsView;
